// ConsultationDetailsForm.jsx
import * as React from 'react';
import { useForm, useFormContext } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Plus, AlertCircle } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { useUserState } from '@/components/context/UserContext';
import { useCartData } from '../../context/CartDataContext';
import { createConsultationDetailsSchema } from './createConsultationDetailsSchema';
import { createContraindicationsSchema } from './createContraindicationsSchema';

// Registration context
import { useRegistration } from '../../context/RegistrationContext';
import { useSettingsState } from '@/components/context/SettingsContext';
import { useFileUpload } from './useFileUpload';
import {
  isConsultationByServiceTypeExtId,
  isConsultationReasonNotShown,
  isMrt,
  isReferralAllwaysRequired,
  isReferralInputsAlwaysShown,
} from '@/v2/utils/commonServiceClassificationRulesUtils';

const ACCEPTED_MIME = ['image/png', 'image/jpeg', 'application/pdf'];

const formatPrice = (eur) =>
  new Intl.NumberFormat('lt-LT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(eur);

function createTrimOnBlur(form) {
  return function trimOnBlur(fieldApi) {
    return (e) => {
      fieldApi.onBlur?.();
      const trimmed = (e.target.value || '').trim();
      if (trimmed !== e.target.value) {
        form.setValue(fieldApi.name, trimmed, {
          shouldValidate: false,
          shouldDirty: true,
        });
      }
    };
  };
}

function FormRadioBlock({
  control,
  name,
  label,
  options = [],
  formatPrice,
  description,
  disabled = false,
  className,
}) {
  const prefix = React.useId();
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          {label && <FormLabel>{label}</FormLabel>}
          {description && (
            <p className="text-sm text-muted-foreground">{description}</p>
          )}
          <FormControl>
            <div className="w-full rounded-lg border overflow-hidden">
              <RadioGroup
                value={field.value ?? ''}
                onValueChange={field.onChange}
                className="grid w-full gap-0 divide-y"
                disabled={disabled}
              >
                {options.map((opt) => {
                  const id = `${prefix}-${opt.id}`;
                  return (
                    <div key={opt.id} className="w-full">
                      <Label
                        htmlFor={id}
                        className="w-full min-h-12 px-4 flex items-center justify-between cursor-pointer"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <RadioGroupItem id={id} value={opt.id} />
                          <span className="whitespace-normal break-words text-left">
                            {opt.label}
                          </span>
                        </div>
                        {typeof opt.priceEur === 'number' && (
                          <div className="font-semibold text-sm whitespace-nowrap">
                            {formatPrice
                              ? formatPrice(opt.priceEur)
                              : `${opt.priceEur} €`}
                          </div>
                        )}
                      </Label>
                    </div>
                  );
                })}
              </RadioGroup>
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

/** Checkbox list block with a single outer border, no inner separators */
function FormCheckboxListBlock({
  control,
  name,
  label,
  items = [],
  className,
  // for on-check validation
  contraSchema,
  hasAdditionalQuestions = false,
  serviceTypeExternalId,
}) {
  const gskRedirectUrlFromEsteticLaserSpecificService =
    process.env.REACT_APP_GSK_EL_REDIRECTURL;
  const prefix = React.useId();

  const { setError, clearErrors, formState } = useFormContext();
  const { agreements, phoneNumber, shortPhoneNumber, email } =
    useSettingsState();
  const { t } = useTranslation();

  const validateNow = React.useCallback(
    (ids) => {
      if (!contraSchema) return;
      const res = contraSchema.safeParse({ contraindicationQuestionIds: ids });
      if (!res.success) {
        const message = res.error.issues?.[0]?.message || 'Neteisinga reikšmė';
        setError(name, { type: 'manual', message });
      } else {
        clearErrors(name);
      }
    },
    [contraSchema, name, setError, clearErrors],
  );

  const fieldError = formState?.errors?.[name];

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const valueArray = Array.isArray(field.value) ? field.value : [];

        const isContraindicationPregnancyAndBirthControlQuestionChecked =
          valueArray.includes(6);
        const toggle = (id, checked) => {
          const idNum = Number(id);
          const exists = valueArray.includes(idNum);
          let next = valueArray;
          if (checked && !exists) next = [...valueArray, idNum];
          if (!checked && exists) next = valueArray.filter((x) => x !== idNum);

          field.onChange(next);
          validateNow(next);
        };

        return (
          <FormItem className={className}>
            {label && <FormLabel>{label}</FormLabel>}
            <FormControl>
              <div className="w-full rounded-lg border p-2 sm:p-3">
                {items.length === 0 ? (
                  <p className="text-sm text-muted-foreground px-2 py-1">
                    Nėra pateiktų kontraindikacijų klausimų.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {items.map(({ id, label }) => {
                      const cid = `${prefix}-${id}`;
                      const checked = valueArray.includes(Number(id));
                      return (
                        <li
                          key={id}
                          className="flex items-start gap-3 text-left px-2"
                        >
                          <Checkbox
                            id={cid}
                            checked={checked}
                            onCheckedChange={(v) => toggle(id, !!v)}
                            className="shrink-0 self-start mt-[2px]"
                          />
                          <Label
                            htmlFor={cid}
                            className="flex-1 text-sm leading-6 cursor-pointer text-left"
                          >
                            {label}
                          </Label>
                        </li>
                      );
                    })}
                  </ul>
                )}

                <div className="mt-3">
                  <Separator className="my-3" />
                  <p className="text-sm text-primary text-left px-2">
                    <strong>SVARBU! </strong>Tyrimo atlikimas turint bent vieną
                    kontraindikaciją gali būti pavojingas žmogaus sveikatai ar
                    net gyvybei. Jei turite klausimų dėl kontraindikacijų,
                    prašome susisiekti tel. {shortPhoneNumber} arba{' '}
                    {phoneNumber}.
                  </p>
                </div>

                {isMrt(serviceTypeExternalId) &&
                  isContraindicationPregnancyAndBirthControlQuestionChecked && (
                    <div className="rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900 text-left mt-3">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 mt-0.5" />
                        <p>
                          {t(
                            'validation.contraindicationPregnancyAndBirthControlQuestion',
                            {
                              shortPhoneNumber,
                              email,
                            },
                          )}
                        </p>
                      </div>
                    </div>
                  )}

                {fieldError && (
                  <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-900 text-left mt-3">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 mt-0.5" />
                      <p>
                        {hasAdditionalQuestions ? (
                          <Trans
                            i18nKey="validation.additioanlContraindicationQuestion"
                            values={{ shortPhoneNumber, email }}
                            components={{
                              a: (
                                <a
                                  className="text-blue-600 underline hover:text-blue-800 cursor-pointer"
                                  href={
                                    gskRedirectUrlFromEsteticLaserSpecificService
                                  }
                                  target="_blank"
                                />
                              ),
                            }}
                          />
                        ) : (
                          t('validation.contraindicationQuestion', {
                            shortPhoneNumber,
                            email,
                          })
                        )}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </FormControl>
          </FormItem>
        );
      }}
    />
  );
}

export default function ConsultationDetailsForm({
  defaultOptionId = 'primary',
  defaultReason = '',
  defaultEHealthReferral = false,
  fileUploadRequired = true,
  onValid,
  onInvalid,
  onBack,
  onContraErrorChange,
}) {
  const userState = useUserState();
  const isDiscount = userState.discountCard;
  const { servicesData, service, selectedTimeSlot, questions } =
    useCartData?.() || {};
  const { t } = useTranslation();

  // Registration context
  const { consultationInfo, setConsultationInfo } = useRegistration();

  const serviceTypeExternalId = process.env.REACT_APP_NGK_SERVICE_TYPE_EXT_ID;
  const serviceTypeIdSearchByKeyword =
    process.env.REACT_APP_SERVICE_TYPE_ID_REQUIRE_PSDF_FILE_BY_SEARCH_KEYWORD;

  const sgkExternalServiceTypeId =
    process.env.REACT_APP_SGK_SERVICE_TYPE_EXT_ID;
  const SGSpecialtyExternalId = process.env.REACT_APP_SG_SPECIALTY_EXTERNAL_ID;
  const kTServiceTypeExternalId = process.env.REACT_APP_KT_SERVICE_TYPE_EXT_ID;

  const svc = React.useMemo(() => {
    if (service && typeof service === 'object') return service;
    if (!servicesData) return undefined;
    return Array.isArray(servicesData) ? servicesData[0] : servicesData;
  }, [service, servicesData]);

  const isPaid = !!selectedTimeSlot?.isPaid;
  const isExtra = !!selectedTimeSlot?.isExtra;
  const isSgkType = svc?.serviceTypeExternalId == sgkExternalServiceTypeId;

  const isMrtLabelContext =
    isMrt(svc?.serviceTypeExternalId) &&
    !!questions?.contraindicationQuestions?.length;

  const showConsultationType =
    svc?.secondaryPrice != null && !isExtra && isPaid;
  const healthCheckServiceTypeExternalId =
    process.env.REACT_APP_HEALTH_CHECK_EXTID;
  const showConsultationReason =
    svc?.serviceTypeExternalId?.toString() !==
      kTServiceTypeExternalId.toString() &&
    svc?.serviceTypeExternalId?.toString() !==
      healthCheckServiceTypeExternalId.toString() &&
    !isConsultationReasonNotShown(svc?.serviceTypeExternalId, isPaid);

  const isHealthCheck =
    svc?.serviceTypeExternalId?.toString() ===
    healthCheckServiceTypeExternalId.toString();

  const consultationOptions = showConsultationType
    ? (() => {
        const primaryPrice = Number(svc?.priceWithDiscount);
        const secondaryPrice = Number(
          isDiscount ? svc?.secondaryDiscountEur : svc?.secondaryPrice,
        );
        return [
          {
            id: 'primary',
            label: 'Pirminė konsultacija',
            priceEur: Number.isFinite(primaryPrice) ? primaryPrice : undefined,
          },
          {
            id: 'secondary',
            label:
              'Pakartotinė konsultacija dėl tos pačios priežasties 1 mėn. laikotarpyje',
            priceEur: Number.isFinite(secondaryPrice)
              ? secondaryPrice
              : undefined,
          },
        ];
      })()
    : [];

  const contraItems = React.useMemo(() => {
    const arr = questions?.contraindicationQuestions;
    if (!Array.isArray(arr)) return [];
    return arr
      .map((q, idx) => {
        const id = q?.id ?? q?.questionId ?? q?.value ?? q?.key ?? idx + 1;
        const label =
          q?.label ??
          q?.question ??
          q?.title ??
          q?.text ??
          q?.name ??
          String(id);
        return { id: Number(id), label: String(label) };
      })
      .filter((it, i, a) => it.id && a.findIndex((x) => x.id === it.id) === i);
  }, [questions?.contraindicationQuestions]);

  const hasContraQs = contraItems.length > 0;

  const isConditionsForAdditionalQuestionService = () =>
    service?.hasAdditionalQuestions;

  const isConditionsMetForMRTContraindications = React.useCallback(() => {
    return (
      isMrt(svc?.serviceTypeExternalId) &&
      Array.isArray(questions?.contraindicationQuestions) &&
      questions.contraindicationQuestions.length > 0
    );
  }, [svc, isMrt, questions]);

  const isFileRequiredForService = React.useCallback(() => {
    if (!svc || Object.keys(svc).length === 0) return false;

    if (
      selectedTimeSlot?.isPaid &&
      isReferralAllwaysRequired(svc.serviceTypeExternalId)
    ) {
      return true;
    }

    if (selectedTimeSlot?.isPaid) {
      return false;
    }

    if (
      !selectedTimeSlot?.isPaid &&
      String(serviceTypeIdSearchByKeyword) ===
        String(svc?.serviceTypeExternalId) &&
      (svc?.serviceTitle?.toLowerCase()?.includes('šeimos') ||
        svc?.specialty?.toLowerCase()?.includes('šeimos'))
    ) {
      return false;
    }
    if (
      !selectedTimeSlot?.isPaid &&
      String(serviceTypeExternalId) !== String(svc?.serviceTypeExternalId)
    ) {
      return true;
    }
    return false;
  }, [
    svc,
    selectedTimeSlot,
    serviceTypeIdSearchByKeyword,
    serviceTypeExternalId,
  ]);

  const Schema = React.useMemo(
    () =>
      createConsultationDetailsSchema({
        service: svc ?? {},
        selectedTimeSlot,
        fileUploadRequired,
        t,
        requiredMessage: t('validation.requiredField'),
        isConditionsMetForMRTContraindications,
        isFileRequiredForService,
        showConsultationReason,
      }),
    [
      svc,
      selectedTimeSlot,
      fileUploadRequired,
      t,
      isConditionsMetForMRTContraindications,
      isFileRequiredForService,
      showConsultationReason,
    ],
  );

  const ContraSchema = React.useMemo(
    () =>
      createContraindicationsSchema({
        t,
        isConditionsMetForMRTContraindications,
        isConditionsForAdditionalQuestionService,
        phoneNumber: userState?.phoneNumber,
        shortPhoneNumber: userState?.shortPhoneNumber,
        email: userState?.email,
      }),
    [
      t,
      isConditionsMetForMRTContraindications,
      isConditionsForAdditionalQuestionService,
      userState?.phoneNumber,
      userState?.shortPhoneNumber,
      userState?.email,
    ],
  );

  const form = useForm({
    resolver: zodResolver(Schema),
    defaultValues: {
      visitType: defaultOptionId,
      reason: defaultReason,
      eHealthReferral: defaultEHealthReferral,
      paidReasonId: undefined,
      files: [],
      contraindicationQuestionIds: [],
    },
    mode: 'onSubmit',
    shouldFocusError: true,
  });

  const { reset } = form;
  const trimOnBlur = React.useMemo(() => createTrimOnBlur(form), [form]);

  const contraError = !!form.formState?.errors?.contraindicationQuestionIds;
  React.useEffect(() => {
    onContraErrorChange?.(contraError);
  }, [contraError, onContraErrorChange]);

  const [showUploader, setShowUploader] = React.useState(false);
  const didHydrate = React.useRef(false);
  React.useEffect(() => {
    if (!consultationInfo || didHydrate.current) return;
    didHydrate.current = true;
    console.log(consultationInfo);
    const legacyToNew = (v) =>
      v === 'opt1' ? 'primary' : v === 'opt2' ? 'secondary' : v;

    const hydratedVisitType =
      legacyToNew(
        consultationInfo.visitType ??
          consultationInfo.selected ??
          defaultOptionId,
      ) || 'primary';

    reset({
      visitType: hydratedVisitType,
      reason: consultationInfo.reason ?? defaultReason,
      eHealthReferral:
        consultationInfo.eHealthReferral ?? defaultEHealthReferral,
      paidReasonId: consultationInfo.paidReasonId ?? undefined,
      files: consultationInfo.files ?? [],
      contraindicationQuestionIds:
        consultationInfo.contraindicationQuestionIds ?? [],
    });
    if (consultationInfo.files?.length) setShowUploader(true);
  }, []);

  const rawServiceQs = questions?.serviceQuestions ?? [];
  const filteredServiceQuestions =
    isPaid && rawServiceQs.length > 0
      ? isSgkType && svc?.isSubscriptionDepartment
        ? rawServiceQs.filter((x) => x.questionId === 3 || x.questionId === 4)
        : !isSgkType
        ? rawServiceQs.filter((x) => x.questionId !== 4)
        : []
      : [];

  const paidReasonOptions = filteredServiceQuestions.map((q) => ({
    id: String(q.questionId),
    label: q.question,
  }));

  const hasOptions = paidReasonOptions.length > 0;

  const showPaidReasonBlock =
    isPaid &&
    ((isSgkType && svc?.isSubscriptionDepartment && hasOptions) ||
      (!isSgkType && svc?.categoryId !== 3 && hasOptions));

  // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
  // Write-through: keep RegistrationContext updated with live visitType
  const visitTypeWatch = form.watch('visitType');
  const contraindicationQuestionIdsWatch =
    form.watch('contraindicationQuestionIds') || [];
  React.useEffect(() => {
    const vt = visitTypeWatch || 'primary';
    setConsultationInfo((prev) => ({ ...prev, visitType: vt }));
  }, [visitTypeWatch, setConsultationInfo]);
  // <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<

  const onSubmit = (values) => {
    const rawVisitType = form.getValues('visitType') || 'primary';
    const next = {
      ...values,
      visitType: rawVisitType,
    };
    setConsultationInfo(next);
    onValid?.(next);
  };

  const fileInputRef = React.useRef(null);
  const [dragActive, setDragActive] = React.useState(false);
  const files = form.watch('files') || [];

  // ⬇️ REPLACED local-only upload/remove with backend-powered hook
  const {
    addFiles,
    removeFile: removeFileById,
    tooManyFilesError,
    uploadError,
    validationError,
    isUploading,
  } = useFileUpload({
    files,
    setFiles: (next) => {
      // keep form state
      form.setValue('files', next, { shouldDirty: true });
      // push to RegistrationContext, including IDs
      setConsultationInfo((prev) => ({
        ...prev,
        files: next,
        fileIds: next.map((f) => f.fileUid).filter(Boolean),
      }));
      // clear field-level error if any
      if (form.formState.errors.files && next.length > 0) {
        form.clearErrors('files');
      }
    },
    onIdsChange: (ids) => {
      // extra safety if you keep fileIds separately
      setConsultationInfo((prev) => ({ ...prev, fileIds: ids }));
    },
    maxFiles: 5,
    acceptedMime: ACCEPTED_MIME,
    maxFileSize: 10 * 1000000,
    // isPopup: false, // enable & pass resizeFile if you need popup-resize behavior
    // resizeFile,
    // notify: (msg, type) => toast(...)
  });

  // Minimal changes in handlers: now delegate to addFiles()
  const handleFilePickerChange = (e) => {
    const list = e.target?.files ? Array.from(e.target.files) : [];
    addFiles(list);
    if (e.target) e.target.value = '';
  };

  const onDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };
  const onDragLeave = () => setDragActive(false);
  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const dropped = e.dataTransfer?.files
      ? Array.from(e.dataTransfer.files)
      : [];
    addFiles(dropped);
  };

  // Keep a tiny helper that delegates to backend delete when possible
  const removeFile = (file) => {
    if (file?.fileUid) {
      removeFileById(file.fileUid);
    } else {
      // fallback: local remove (shouldn't happen once everything is uploaded)
      const curr = form.getValues('files') || [];
      const next = curr.filter((f) => f.name !== file?.name);
      form.setValue('files', next, { shouldDirty: true });
      setConsultationInfo((prev) => ({
        ...prev,
        files: next,
        fileIds: next.map((f) => f.fileUid).filter(Boolean),
      }));
    }
  };

  const handleSubmitWithScroll = async (e) => {
    e.preventDefault();
    const result = await form.handleSubmit(onSubmit, onInvalid)(e);

    // Wait a tick so errors render
    setTimeout(() => {
      const firstErrorKey = Object.keys(form.formState.errors)[0];
      if (firstErrorKey) {
        const el = document.querySelector(`[name="${firstErrorKey}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.focus({ preventScroll: true });
        }
      }
    }, 150);
  };

  return (
    <Form {...form}>
      <form
        id="consultation-details-form-id"
        // onSubmit={form.handleSubmit(onSubmit, onInvalid)}
        onSubmit={handleSubmitWithScroll}
        className="w-full space-y-6"
      >
        {hasContraQs && (
          <section className="w-full space-y-3">
            <FormCheckboxListBlock
              control={form.control}
              name="contraindicationQuestionIds"
              label="Kontraindikacijos (pažymėkite, jei turite bent vieną)"
              items={contraItems}
              contraSchema={ContraSchema}
              hasAdditionalQuestions={isConditionsForAdditionalQuestionService()}
              serviceTypeExternalId={svc?.serviceTypeExternalId}
            />
          </section>
        )}

        {showConsultationType && consultationOptions.length > 0 && (
          <section className="w-full space-y-3">
            <FormRadioBlock
              control={form.control}
              name="visitType"
              label="Pasirinkite konsultacijos tipą"
              options={consultationOptions}
              formatPrice={formatPrice}
            />
          </section>
        )}
        {!!showConsultationReason && (
          <section className="w-full space-y-2">
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {isConsultationByServiceTypeExtId(
                      service?.serviceTypeExternalId,
                    )
                      ? 'Konsultacijos priežastis'
                      : isMrt(svc?.serviceTypeExternalId)
                      ? 'Papildoma informacija'
                      : 'Vizito priežastis'}
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder={
                        isMrtLabelContext
                          ? 'Išsami informacija apie pažymėtas kontraindikacijas arba kitos reikšmingos pastabos (nusiskundimai, tyrimo tikslas ir kt.)'
                          : 'Trumpai aprašykite nusiskundimus.'
                      }
                      className="min-h-[110px] w-full"
                      onBlur={trimOnBlur(field)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </section>
        )}

        {isHealthCheck && (
          <section className="w-full p-4 rounded-lg bg-blue-50 text-primary">
            Atsižvelgiant į rizikos faktorius, taip pat tikrinamojo asmens
            sveikatos būklę, gali būti reikalingos papildomos paslaugos
            (papildomi ištyrimai, kitų gydytojų konsultacijos, pažymos) už
            kurias pacientams klinikoje reikia susimokėti papildomai. Jeigu
            sutinkate, spauskite „Pereiti prie apmokėjimo“.
          </section>
        )}

        {service?.specialtyExternalId != SGSpecialtyExternalId &&
          (!isPaid ||
            isReferralInputsAlwaysShown(service?.serviceTypeExternalId) ||
            (isMrt(svc?.serviceTypeExternalId) &&
              contraindicationQuestionIdsWatch?.length > 0)) && (
            <section className="w-full space-y-3">
              <div className="w-full flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <Label>
                  {isMrt(svc?.serviceTypeExternalId)
                    ? 'Pridėkite siuntimą, medicininius išrašus apie kūne esančius implantus.'
                    : 'Pridėkite siuntimą.'}
                </Label>

                <div className="flex sm:shrink-0">
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    multiple
                    accept={ACCEPTED_MIME.join(',')}
                    onChange={handleFilePickerChange}
                  />
                  <Button
                    type="button"
                    variant="light"
                    className="h-8 px-3"
                    onClick={() => setShowUploader(true)}
                    disabled={isUploading}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Pridėti
                  </Button>
                </div>
              </div>

              {(showUploader || files.length > 0) && (
                <div
                  className={`w-full rounded-lg border border-dashed bg-muted/30 px-4 py-6 text-center text-sm text-muted-foreground ${
                    dragActive ? 'ring-2 ring-primary/30' : ''
                  }`}
                  onDragOver={onDragOver}
                  onDragLeave={onDragLeave}
                  onDrop={onDrop}
                >
                  <div className="flex flex-wrap items-center justify-center gap-2">
                    <span>Tempti failą čia (PNG, JPEG, PDF) arba</span>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                    >
                      Pasirinkti iš įrenginio
                    </Button>
                  </div>
                </div>
              )}

              {/* Inline error feedback (minimal addition) */}
              {(tooManyFilesError || uploadError || validationError) && (
                <p className="text-sm text-red-600">
                  {validationError ||
                    uploadError ||
                    'Pasiekta leidžiamų failų riba.'}
                </p>
              )}

              <FormField
                control={form.control}
                name="files"
                render={() => (
                  <FormItem>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="eHealthReferral"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        id="ehealth"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel htmlFor="ehealth" className="leading-none">
                      Siuntimas yra E-sveikatoje
                    </FormLabel>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {files.length > 0 && (
                <div className="flex flex-wrap gap-2 w-full">
                  {files.map((f) => {
                    const displayName = f.name ?? f.fileName ?? 'failas';
                    const key = f.fileUid ?? `${displayName}-${f.size ?? ''}`;
                    return (
                      <Badge
                        key={key}
                        variant="secondary"
                        className="flex items-center gap-2 rounded-md"
                      >
                        <span className="text-xs">{displayName}</span>
                        <button
                          type="button"
                          className="text-xs opacity-70 hover:opacity-100"
                          onClick={() => removeFile(f)}
                          aria-label={`Pašalinti ${displayName}`}
                        >
                          ×
                        </button>
                      </Badge>
                    );
                  })}
                </div>
              )}
            </section>
          )}
        {showPaidReasonBlock && (
          <section className="w-full space-y-3">
            <FormRadioBlock
              control={form.control}
              name="paidReasonId"
              label="Prašome pažymėti, kodėl paslaugoms registruojatės mokama tvarka:"
              options={paidReasonOptions}
            />
          </section>
        )}
      </form>
    </Form>
  );
}
